/**
 * Shared visual tokens for the buyer-facing card surfaces (TNK-115).
 *
 * Used by `/shop/card/$id` (full-page detail) and `CardDetailModal` (in-grid
 * quick-look). Lives in a plain module instead of a React context because:
 *   - All values are static lookups, not state.
 *   - Both surfaces care about the same dimension reductions (Rarity, Game,
 *     Condition, Pokemon type) — they're shopping for the same data.
 *
 * Inline-only values are still inline at the call site. This module just
 * carries the lookup tables so they're not duplicated between page + modal.
 */
import type { ReactNode } from "react";
import {
  Dices,
  Droplets,
  Flame,
  Leaf,
  Shield,
  Star,
  Wind,
  Zap,
} from "lucide-react";

// ─── Types ──────────────────────────────────────────────────────────────────

export type Rarity =
  | "Common"
  | "Uncommon"
  | "Rare"
  | "Mythic"
  | "Promo"
  | "Trainer"
  | "Energy"
  | "Damaged"
  | "Sealed";

export type ConditionCode = "NM" | "LP" | "MP" | "HP" | "Damaged";

export type Game = "Pokemon" | "Magic" | "Yu-Gi-Oh" | "One Piece" | "Other";

// ─── Lookups ────────────────────────────────────────────────────────────────

export const rarityConfig: Record<Rarity, { color: string; glow: string }> = {
  Common: { color: "#7a8aaa", glow: "rgba(122,138,170,0.25)" },
  Uncommon: { color: "#4ade80", glow: "rgba(74,222,128,0.25)" },
  Rare: { color: "#4d9ef7", glow: "rgba(77,158,247,0.3)" },
  Mythic: { color: "#f0c96e", glow: "rgba(240,201,110,0.4)" },
  Promo: { color: "#a855f7", glow: "rgba(168,85,247,0.35)" },
  Trainer: { color: "#38bdf8", glow: "rgba(56,189,248,0.3)" },
  Energy: { color: "#fb923c", glow: "rgba(251,146,60,0.3)" },
  Damaged: { color: "#f87171", glow: "rgba(248,113,113,0.25)" },
  Sealed: { color: "#c9a84c", glow: "rgba(201,168,76,0.35)" },
};

export const conditionConfig: Record<
  ConditionCode,
  { label: string; color: string; desc: string }
> = {
  NM: { label: "Near Mint", color: "#4ade80", desc: "No visible wear." },
  LP: {
    label: "Lightly Played",
    color: "#a3e635",
    desc: "Minor edge nicks or light scuffs.",
  },
  MP: {
    label: "Moderately Played",
    color: "#facc15",
    desc: "Noticeable whitening, creases, or edge wear.",
  },
  HP: {
    label: "Heavily Played",
    color: "#fb923c",
    desc: "Significant wear — still sleeveable.",
  },
  Damaged: {
    label: "Damaged",
    color: "#f87171",
    desc: "Major damage affecting appearance.",
  },
};

export const gameAccent: Record<Game, { primary: string; bg: string }> = {
  Pokemon: { primary: "#ff6b6b", bg: "rgba(255,107,107,0.08)" },
  Magic: { primary: "#a78bfa", bg: "rgba(167,139,250,0.08)" },
  "Yu-Gi-Oh": { primary: "#fcd34d", bg: "rgba(252,211,77,0.08)" },
  "One Piece": { primary: "#f97316", bg: "rgba(249,115,22,0.08)" },
  Other: { primary: "#7a8aaa", bg: "rgba(122,138,170,0.08)" },
};

export const pokeTypeColor: Record<
  string,
  { bg: string; text: string; icon: ReactNode }
> = {
  Fire: {
    bg: "rgba(251,146,60,0.18)",
    text: "#fb923c",
    icon: <Flame size={11} />,
  },
  Water: {
    bg: "rgba(96,165,250,0.18)",
    text: "#60a5fa",
    icon: <Droplets size={11} />,
  },
  Lightning: {
    bg: "rgba(250,204,21,0.18)",
    text: "#facc15",
    icon: <Zap size={11} />,
  },
  Psychic: {
    bg: "rgba(232,121,249,0.18)",
    text: "#e879f9",
    icon: <Star size={11} />,
  },
  Fighting: {
    bg: "rgba(251,113,133,0.18)",
    text: "#fb7185",
    icon: <Shield size={11} />,
  },
  Grass: {
    bg: "rgba(74,222,128,0.18)",
    text: "#4ade80",
    icon: <Leaf size={11} />,
  },
  Colorless: {
    bg: "rgba(148,163,184,0.18)",
    text: "#94a3b8",
    icon: <Dices size={11} />,
  },
  Darkness: {
    bg: "rgba(99,102,241,0.18)",
    text: "#818cf8",
    icon: <Star size={11} />,
  },
  Metal: {
    bg: "rgba(203,213,225,0.18)",
    text: "#cbd5e1",
    icon: <Shield size={11} />,
  },
  Wind: {
    bg: "rgba(125,211,252,0.18)",
    text: "#7dd3fc",
    icon: <Wind size={11} />,
  },
};

// ─── Narrowers ──────────────────────────────────────────────────────────────

export function asRarity(r: string): Rarity {
  return (r in rarityConfig ? r : "Common") as Rarity;
}
export function asCondition(c: string): ConditionCode {
  return (c in conditionConfig ? c : "NM") as ConditionCode;
}
export function asGame(g: string): Game {
  return (g in gameAccent ? g : "Other") as Game;
}
